import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { TrendingUp, Target, CheckCircle2, XCircle, Star, Zap, Clock, BarChart3, Lightbulb, Award, Loader2, RefreshCw } from "lucide-react";
import type { Outcome } from "@shared/schema";

interface PerformanceData {
  summary: {
    totalOutcomes: number;
    successRate: number;
    avgRating: number;
    totalCreditsUsed: number;
    activeMissions: number;
    completedMissions: number;
    completedRuns: number;
    failedRuns: number;
  };
  recentOutcomes: Outcome[];
  topLessons: string[];
}

export default function PerformanceInsights() {
  const { toast } = useToast();
  const [timeRange, setTimeRange] = useState("all");

  const { data: insights, isLoading, refetch } = useQuery<PerformanceData>({
    queryKey: ["/api/performance-insights", timeRange],
  });

  const { data: outcomes = [] } = useQuery<Outcome[]>({
    queryKey: ["/api/outcomes"],
  });

  const filterByTimeRange = (date: string | Date) => {
    const itemDate = new Date(date);
    const now = new Date();
    
    switch (timeRange) {
      case "week":
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return itemDate >= weekAgo;
      case "month":
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return itemDate >= monthAgo;
      case "quarter":
        const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        return itemDate >= quarterAgo;
      default:
        return true;
    }
  };

  const filteredOutcomes = outcomes.filter(o => filterByTimeRange(o.createdAt));

  const isSuccessful = (success: string | boolean): boolean => {
    return success === true || success === "true";
  };

  const isFailed = (success: string | boolean): boolean => {
    return success === false || success === "false";
  };

  const getSuccessBadgeVariant = (success: string | boolean): "default" | "destructive" | "secondary" => {
    if (isSuccessful(success)) return "default";
    if (isFailed(success)) return "destructive";
    return "secondary";
  };

  const getSuccessIcon = (success: string | boolean) => {
    if (isSuccessful(success)) {
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    } else if (isFailed(success)) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    } else {
      return <Zap className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getSuccessLabel = (success: string | boolean) => {
    if (isSuccessful(success)) {
      return "Success";
    } else if (isFailed(success)) {
      return "Failed";
    } else {
      return "Partial";
    }
  };

  const getRatingStars = (rating: string | null) => {
    if (!rating) return null;
    const numRating = parseInt(rating);
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`h-3 w-3 ${i <= numRating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const summary = insights?.summary || {
    totalOutcomes: 0,
    successRate: 0,
    avgRating: 0,
    totalCreditsUsed: 0,
    activeMissions: 0,
    completedMissions: 0,
    completedRuns: 0,
    failedRuns: 0,
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Performance Insights</h1>
          <p className="text-muted-foreground mt-1">Track outcomes and learn from your automation workflows</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]" data-testid="select-time-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => refetch()} data-testid="button-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.successRate}%</p>
                <p className="text-sm text-muted-foreground">Success Rate</p>
              </div>
            </div>
            <Progress value={summary.successRate} className="mt-3 h-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Star className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.avgRating.toFixed(1)}</p>
                <p className="text-sm text-muted-foreground">Avg Rating</p>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Star
                  key={i}
                  className={`h-4 w-4 ${i <= Math.round(summary.avgRating) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`}
                />
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Target className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.completedMissions}</p>
                <p className="text-sm text-muted-foreground">Missions Completed</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {summary.activeMissions} currently active
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Zap className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.totalCreditsUsed}</p>
                <p className="text-sm text-muted-foreground">Credits Used</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Across {summary.totalOutcomes} outcomes
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Workflow Performance
              </CardTitle>
              <CardDescription>Overview of your playbook and mission outcomes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-4">Playbook Runs</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Completed</span>
                      </div>
                      <span className="font-medium">{summary.completedRuns}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="text-sm">Failed</span>
                      </div>
                      <span className="font-medium">{summary.failedRuns}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Success Rate</span>
                      </div>
                      <span className="font-medium">
                        {summary.completedRuns + summary.failedRuns > 0
                          ? Math.round((summary.completedRuns / (summary.completedRuns + summary.failedRuns)) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-4">Agent Missions</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Completed</span>
                      </div>
                      <span className="font-medium">{summary.completedMissions}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">Active</span>
                      </div>
                      <span className="font-medium">{summary.activeMissions}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Total Logged</span>
                      </div>
                      <span className="font-medium">{summary.totalOutcomes}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recent Outcomes
              </CardTitle>
              <CardDescription>Latest results from your workflows and missions</CardDescription>
            </CardHeader>
            <CardContent>
              {(!insights?.recentOutcomes || insights.recentOutcomes.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Award className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No outcomes recorded yet</p>
                  <p className="text-sm">Complete missions and playbooks to see results here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {insights.recentOutcomes.map(outcome => (
                    <div key={outcome.id} className="flex items-start gap-3 p-3 border rounded-lg" data-testid={`outcome-${outcome.id}`}>
                      {getSuccessIcon(outcome.success)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{outcome.entityTitle || outcome.entityType}</span>
                          <Badge variant="outline">{outcome.entityType}</Badge>
                          <Badge variant={getSuccessBadgeVariant(outcome.success)}>
                            {getSuccessLabel(outcome.success)}
                          </Badge>
                        </div>
                        {outcome.feedback && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{outcome.feedback}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {getRatingStars(outcome.rating)}
                          {outcome.creditsUsed && parseInt(outcome.creditsUsed) > 0 && (
                            <span>{outcome.creditsUsed} credits</span>
                          )}
                          {outcome.durationMinutes && (
                            <span>{outcome.durationMinutes} min</span>
                          )}
                          <span>{new Date(outcome.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                Lessons Learned
              </CardTitle>
              <CardDescription>Key insights from your workflow experiences</CardDescription>
            </CardHeader>
            <CardContent>
              {(!insights?.topLessons || insights.topLessons.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No lessons recorded yet</p>
                  <p className="text-sm mt-1">Add feedback to your outcomes to capture learnings</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {insights.topLessons.map((lesson, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-sm">{lesson}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Outcomes</span>
                  <span className="font-medium">{filteredOutcomes.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Successful</span>
                  <span className="font-medium text-green-500">
                    {filteredOutcomes.filter(o => o.success === true || o.success === "true").length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Partial</span>
                  <span className="font-medium text-yellow-500">
                    {filteredOutcomes.filter(o => o.success === "partial").length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Failed</span>
                  <span className="font-medium text-red-500">
                    {filteredOutcomes.filter(o => o.success === false || o.success === "false").length}
                  </span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Avg Credits/Outcome</span>
                    <span className="font-medium">
                      {filteredOutcomes.length > 0
                        ? Math.round(filteredOutcomes.reduce((sum, o) => sum + parseInt(o.creditsUsed || "0"), 0) / filteredOutcomes.length)
                        : 0}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
